import { IHomeTicket, ITicketListItem } from "../types/Ticket/Ticket";

export type TicketDateInput = string | number | Date | null | undefined;

export enum TicketState {
    New = 'New',
    Current = 'Current',
    AtRisk = 'At Risk',
    AtRiskEOD = 'At Risk EOD',
    Late = 'Late',
    LateEOD = 'Late EOD',
    Closed = 'Closed',
}
/**
 * Parse a date-like input into a Date or null if invalid.
 */
function toDate(d: TicketDateInput): Date | null {
    if (d == null) return null;
    const dt = d instanceof Date ? d : new Date(d + 'Z');
    return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Returns the ticket state based on provided dates and the "new" threshold in hours.
 *
 * Rules (checked in priority order):
 * 1. Closed: now >= Close_Dt (if Close_Dt provided)
 * 2. Late: now > Due_Dt (if Due_Dt provided)
 * 3. At Risk: now > At_Risk_Dt (if At_Risk_Dt provided)
 * 4. New: time since Open_Dt < newThresholdHours (if Open_Dt provided)
 * 5. Current: everything else
 *
 * All dates are compared using JS Date semantics (use ISO strings or Date objects to avoid TZ issues).
 *
 * @param params.openDt Open_Dt for the ticket
 * @param params.dueDt Due_Dt for the ticket
 * @param params.atRiskDt At_Risk_Dt for the ticket
 * @param params.closeDt Close_Dt for the ticket
 * @param params.newThresholdHours number of hours that define a "New" ticket
 * @param params.now optional override for the current time (useful for tests)
 */
export function getTicketState(params: {
    homeTicket: ITicketListItem | IHomeTicket;
    newCallThresholdMins: number;
    now?: Date;
}): TicketState {
    const { homeTicket, newCallThresholdMins, now = new Date() } = params;
    const nowTime = now.getTime();
    const { Open_Dt, Due_Dt, At_Risk_Dt, Close_Dt } = homeTicket;
    const openDate = toDate(Open_Dt);
    const dueDate = toDate(Due_Dt);
    const atRiskDate = toDate(At_Risk_Dt);
    const closeDate = toDate(Close_Dt);


    //console.log('getTicketState', homeTicket.Call_Id, Open_Dt, newCallThresholdMins);

    // Closed
    if (closeDate && nowTime >= closeDate.getTime()) return TicketState.Closed;

    // Late
    if (dueDate && nowTime > dueDate.getTime()) return TicketState.Late;

    // At Risk
    if (atRiskDate && nowTime > atRiskDate.getTime()) return TicketState.AtRisk;

    // New (open less than threshold hours)
    if (openDate && typeof newCallThresholdMins === 'number' && newCallThresholdMins > 0) {
        const minOpen = (nowTime - openDate.getTime()) / (1000 * 60);
        if (minOpen >= 0 && minOpen < newCallThresholdMins) return TicketState.New;
    }

    return TicketState.Current;
}